import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { SPANISH_ABBREV } from "@bto/shared";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  CreateAccountCenterContainerPaddedTop,
  Spinner,
} from "../CreateAccount/CreateAccountForm.styles";
import { Button } from "../shared/Button/Button";
import { LOGIN_PAGE_URL } from "../../utils/urlConstants";
import loading from "../../assets/icons/loading.svg";
import { getUserAuth } from "../../recoil/selectors";
import { patientState } from "../../recoil/atoms";
import { getCookieAccessToken } from "../../utils/requestUtils";
import { UpdateLanguagePreference } from "../Profile/Services/UpdateProfileInfoService";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { consumePostAuthPath } from "../../utils/postAuthRedirect";

interface VerifyEmailConfirmedProps {
  email: string;
  canSetSpanishPreferred?: boolean;
}

export const VerifyEmailConfirmedSection = (
  props: VerifyEmailConfirmedProps,
): React.ReactElement => {
  const { t } = useTranslation();
  // Gate the opt-in link's render (not just the arrived-via-Spanish flag) so it
  // re-evaluates when the flag resolves from its initial loading state.
  const { enabled: isLanguageToggleEnabled } = useBooleanFeatureFlag(
    FeatureFlags.PatientPortalSpanishLanguageToggle,
  );
  const history = useHistory();
  const auth = useRecoilValue(getUserAuth);
  const setPatientInfo = useSetRecoilState(patientState);
  const [savingSpanishPreferred, setSavingSpanishPreferred] = useState(false);
  const [spanishPreferredSaved, setSpanishPreferredSaved] = useState(false);
  const [spanishPreferredError, setSpanishPreferredError] = useState(false);

  const handleSetSpanishPreferred = async () => {
    const accessToken = getCookieAccessToken();
    if (!accessToken || savingSpanishPreferred) {
      return;
    }
    setSavingSpanishPreferred(true);
    setSpanishPreferredError(false);
    const response = await UpdateLanguagePreference(
      SPANISH_ABBREV,
      accessToken,
    ).catch(() => undefined);
    setSavingSpanishPreferred(false);
    if (response && response.ok) {
      // Keep client state in sync with the backend so AppContainer's
      // apply-on-login effect applies the saved Spanish preference (not a stale
      // "en") once the patient leaves this page.
      setPatientInfo((prev) =>
        prev
          ? { ...prev, user: { ...prev.user, language: SPANISH_ABBREV } }
          : prev,
      );
      setSpanishPreferredSaved(true);
    } else {
      setSpanishPreferredError(true);
    }
  };

  if (!props.email) {
    return (
      <Spinner>
        <img src={loading} alt="Loading" />
      </Spinner>
    );
  }

  return (
    <CreateAccountCenterContainerPaddedTop>
      {GetTypographyContent({
        content: t("verifyEmail.confirmed.title"),
        size: "h3",
        isBold: true,
        textColor: "neutral700",
      })}
      <br />
      {GetTypographyContent({
        content: t("verifyEmail.confirmed.emailPrefix"),
        size: "medium",
        textColor: "neutral700",
      })}
      {GetTypographyContent({
        content: props.email,
        size: "medium",
        isBold: true,
        textColor: "greenDark",
      })}
      {GetTypographyContent({
        content: t("verifyEmail.confirmed.activated"),
        size: "medium",
        textColor: "neutral700",
        padding: "0px 0 25px 0",
      })}
      {auth ? (
        <Button
          type="submit"
          label={t("verifyEmail.confirmed.continueToAccount")}
          size="medium"
          onClick={() => history.push(consumePostAuthPath())}
        />
      ) : (
        <Button
          type="submit"
          label={t("verifyEmail.confirmed.login")}
          size="medium"
          onClick={() => history.push(LOGIN_PAGE_URL)}
        />
      )}
      {props.canSetSpanishPreferred && isLanguageToggleEnabled && (
        <div style={{ paddingTop: "20px" }}>
          {spanishPreferredSaved ? (
            GetTypographyContent({
              content: t("verifyEmail.confirmed.spanishPreferredSet"),
              size: "medium",
              isBold: true,
              textColor: "greenDark",
            })
          ) : (
            <button
              type="button"
              onClick={handleSetSpanishPreferred}
              disabled={savingSpanishPreferred}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: savingSpanishPreferred ? "default" : "pointer",
                textDecoration: "underline",
              }}
            >
              {GetTypographyContent({
                content: t("verifyEmail.confirmed.setSpanishPreferred"),
                size: "medium",
                textColor: "greenDark",
              })}
            </button>
          )}
          {spanishPreferredError &&
            GetTypographyContent({
              content: t("verifyEmail.confirmed.setSpanishPreferredError"),
              size: "small",
              textColor: "redDark",
              padding: "8px 0 0 0",
            })}
        </div>
      )}
    </CreateAccountCenterContainerPaddedTop>
  );
};
